import { useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Progress } from '../../../components/ui/progress'

interface PoolRollout {
  id: number
  name: string
  target_count: number
  created_count: number
  failed_count: number
  status: string
  instances_per_minute: number
  source_repo_name: string
  started_at: string | null
  paused_at: string | null
  completed_at: string | null
  created_at: string
  progress_percentage: number
  estimated_completion_at: string | null
  estimated_hours: number
  remaining_count: number
  effective_rate: number | null
}

interface PoolStats {
  total_pool: number
  active: number
  provisioning: number
  failed: number
}

interface AdminPoolRolloutsIndexProps {
  rollouts: PoolRollout[]
  pool_stats: PoolStats
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Index({ rollouts, pool_stats }: AdminPoolRolloutsIndexProps) {
  // Auto-refresh every 10 seconds if there are running rollouts
  useEffect(() => {
    const hasRunning = rollouts.some(r => r.status === 'running')
    if (hasRunning) {
      const interval = setInterval(() => {
        router.reload({ only: ['rollouts', 'pool_stats'] })
      }, 10000)
      return () => clearInterval(interval)
    }
  }, [rollouts])

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'pending': 'secondary',
      'running': 'info',
      'paused': 'warning',
      'completed': 'success',
      'cancelled': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {status}
      </Badge>
    )
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Pool Rollouts' }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Pool Rollouts</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Manage mass provisioning of dev instance pool
                  </p>
                </div>
                <Button onClick={() => router.visit('/admin/pool_rollouts/new')}>
                  New Rollout
                </Button>
              </div>

              {/* Pool Stats Dashboard */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="shadow-none">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-foreground">{pool_stats.total_pool}</div>
                    <p className="text-xs text-muted-foreground">Total Pool</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{pool_stats.active}</div>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">{pool_stats.provisioning}</div>
                    <p className="text-xs text-muted-foreground">Provisioning</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-red-600">{pool_stats.failed}</div>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Rollouts List */}
              <div className="space-y-4">
                {rollouts.length > 0 ? (
                  rollouts.map((rollout) => (
                    <Card key={rollout.id} className="shadow-none">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-lg">{rollout.name}</CardTitle>
                            {getStatusBadge(rollout.status)}
                          </div>
                          <Link
                            href={`/admin/pool_rollouts/${rollout.id}`}
                            className="text-sm text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </Link>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Progress Bar */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-muted-foreground">
                                {rollout.created_count.toLocaleString()} / {rollout.target_count.toLocaleString()} instances
                              </span>
                              <span className="text-sm font-medium">
                                {rollout.progress_percentage}%
                              </span>
                            </div>
                            <Progress value={rollout.progress_percentage} className="h-2" />
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Rate</p>
                              <p className="font-medium">{rollout.instances_per_minute}/min</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Failed</p>
                              <p className="font-medium text-red-600">{rollout.failed_count}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Est. Hours</p>
                              <p className="font-medium">{rollout.estimated_hours}h</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Started</p>
                              <p className="font-medium">{formatDateTime(rollout.started_at)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">
                                {rollout.status === 'completed' ? 'Completed' : 'ETA'}
                              </p>
                              <p className="font-medium">
                                {rollout.status === 'completed'
                                  ? formatDateTime(rollout.completed_at)
                                  : formatDateTime(rollout.estimated_completion_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card className="shadow-none">
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">No pool rollouts yet</p>
                      <Button
                        className="mt-4"
                        onClick={() => router.visit('/admin/pool_rollouts/new')}
                      >
                        Create First Rollout
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
